// src/components/LifecycleEventForm.js

import React, { useState } from 'react';
import { Loader, CheckCircle, AlertTriangle } from 'lucide-react';

const PRIMARY = '#326BDF';

const REPAIR_STATUSES = ['In Progress', 'Completed', 'Cannot Repair'];

const DISASSEMBLY_STAGES = ['Received', 'Disassembling', 'Components Sorted', 'Complete'];

const DISPOSAL_METHODS = ['Mechanical', 'Chemical', 'Manual'];

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

// ── Repairer Form ─────────────────────────────────────────────────────────────
function RepairerForm({ dppId, locationId, onSuccess }) {
  const [component, setComponent]   = useState('');
  const [status, setStatus]         = useState('');
  const [notes, setNotes]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const handleSubmit = async () => {
    if (!component || !status) { setError('Please fill all required fields.'); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');

      const res = await fetch(`${API_BASE_URL}/lifecycle-event/${dppId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type:  'Repair',
          location_id: locationId || '',
          event_data: {
            component,
            repair_status: status,
            notes: notes.trim() || undefined,
          }
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit event');

      setComponent(''); setStatus(''); setNotes('');
      onSuccess('Repair event submitted successfully!');
    } catch (err) {
      setError(err.message || 'Failed to submit event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#B91C1C', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Component */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Component Being Repaired <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <input
          type="text"
          value={component}
          onChange={e => setComponent(e.target.value)}
          placeholder="e.g. Display Panel, Power Board, Speaker..."
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: `1.5px solid ${component ? PRIMARY : '#D1D5DB'}`,
            fontSize: 13, color: '#111827', outline: 'none',
            boxSizing: 'border-box', fontFamily: 'inherit'
          }}
          onFocus={e => e.target.style.borderColor = PRIMARY}
          onBlur={e => e.target.style.borderColor = component ? PRIMARY : '#D1D5DB'}
        />
      </div>

      {/* Repair Status */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Repair Status <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {REPAIR_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${status === s ? PRIMARY : '#D1D5DB'}`,
                backgroundColor: status === s ? `${PRIMARY}15` : '#fff',
                color: status === s ? PRIMARY : '#6B7280',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Technician Notes <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Describe the issue or observations..."
          rows={3}
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: '1.5px solid #D1D5DB', fontSize: 13, color: '#111827',
            outline: 'none', resize: 'vertical', fontFamily: 'inherit',
            boxSizing: 'border-box'
          }}
          onFocus={e => e.target.style.borderColor = PRIMARY}
          onBlur={e => e.target.style.borderColor = '#D1D5DB'}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          backgroundColor: loading ? '#93C5FD' : PRIMARY,
          color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          alignSelf: 'flex-start', transition: 'background 0.15s'
        }}
      >
        {loading ? <><Loader size={14} className="animate-spin" />Submitting...</> : 'Submit Repair Event'}
      </button>
    </div>
  );
}

// ── Recycler Form ─────────────────────────────────────────────────────────────
function RecyclerForm({ dppId, locationId, onSuccess }) {
  const [stage, setStage]                   = useState('');
  const [selectedMaterials, setMaterials]   = useState('');
  const [disposalMethod, setDisposalMethod] = useState('');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);

  const handleSubmit = async () => {
    if (!stage || !disposalMethod) { setError('Please fill all required fields.'); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');

      const res = await fetch(`${API_BASE_URL}/lifecycle-event/${dppId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type:  'Recycle',
          location_id: locationId || '',
          event_data: {
            disassembly_stage:   stage,
            materials_recovered: selectedMaterials.trim() || undefined,
            disposal_method:     disposalMethod,
          }
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit event');

      setStage(''); setMaterials(''); setDisposalMethod('');
      onSuccess('Recycling event submitted successfully!');
    } catch (err) {
      setError(err.message || 'Failed to submit event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#B91C1C', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Disassembly Stage */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Disassembly Stage <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DISASSEMBLY_STAGES.map(s => (
            <button
              key={s}
              onClick={() => setStage(s)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${stage === s ? PRIMARY : '#D1D5DB'}`,
                backgroundColor: stage === s ? `${PRIMARY}15` : '#fff',
                color: stage === s ? PRIMARY : '#6B7280',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Materials Recovered */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Materials Recovered <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={selectedMaterials}
          onChange={e => setMaterials(e.target.value)}
          placeholder="e.g. Copper, Plastic, Glass, Rare Earth Metals..."
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: '1.5px solid #D1D5DB', fontSize: 13, color: '#111827',
            outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
          }}
          onFocus={e => e.target.style.borderColor = PRIMARY}
          onBlur={e => e.target.style.borderColor = '#D1D5DB'}
        />
      </div>

      {/* Disposal Method */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Disposal Method <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <select
          value={disposalMethod}
          onChange={e => setDisposalMethod(e.target.value)}
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: `1.5px solid ${disposalMethod ? PRIMARY : '#D1D5DB'}`,
            fontSize: 13, color: '#111827', outline: 'none',
            backgroundColor: '#fff', cursor: 'pointer'
          }}
        >
          <option value="">Select method...</option>
          {DISPOSAL_METHODS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          backgroundColor: loading ? '#93C5FD' : PRIMARY,
          color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          alignSelf: 'flex-start', transition: 'background 0.15s'
        }}
      >
        {loading ? <><Loader size={14} className="animate-spin" />Submitting...</> : 'Submit Recycling Event'}
      </button>
    </div>
  );
}

// ── Main LifecycleEventForm ───────────────────────────────────────────────────
function LifecycleEventForm({ dppId, userRole, locationId }) {
  const [successMsg, setSuccessMsg] = useState(null);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const isRepairer = userRole === 'Repair/Refurb';
  const isRecycler = userRole === 'Recycler';

  if (!isRepairer && !isRecycler) return null;

  return (
    <div style={{
      marginTop: 8,
      background: '#fff',
      border: '1px solid #E2EAF8',
      borderRadius: 12,
      padding: 24,
      boxShadow: '0 1px 4px rgba(50,107,223,0.06)',
    }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
          {isRepairer ? 'Log Repair Event' : 'Log Recycling Event'}
        </h3>
        <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
          {isRepairer
            ? 'Record what component you are working on and the current repair status.'
            : 'Record the current disassembly stage and materials recovered.'}
        </p>
      </div>

      {/* Success message */}
      {successMsg && (
        <div style={{
          background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, color: '#15803D',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16
        }}>
          <CheckCircle size={14} />
          {successMsg}
        </div>
      )}

      {/* Form */}
      {isRepairer && (
        <RepairerForm dppId={dppId} locationId={locationId} onSuccess={handleSuccess} />
      )}
      {isRecycler && (
        <RecyclerForm dppId={dppId} locationId={locationId} onSuccess={handleSuccess} />
      )}
    </div>
  );
}

export default LifecycleEventForm;